import { useAuth } from '@agentnet/auth';
import { getPrefferedOffices } from 'api/admin/preferred-offices-api';
import { getProfileDetails } from 'api/admin/user-profile-api';
import { PrismUserProfile } from 'api/prismPropertyProfile/types';
import { getProfile, reactivateCurrentUser } from 'api/profile/profile-api';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ADMIN_ROLE_ID, getDefaultFirm } from '../api/profile/profile-utilities';
import { Firm, Office, Profile } from '../api/profile/types';
import useAsync, { NoSessionError } from './useAsync';
import { BaggageKeys, useTelemetry } from '../telemetry/useTelemetry';
import { getFullName } from 'utilities/utilities';
import { AsyncStatus } from 'features/admin/manageUsers/manageUsers';

export interface ProfileContextInterface {
  userFirm?: Firm | null;
  currentOffice?: Office | null;
  setUserFirm?: (userFirm: Firm) => void;
  setCurrentOffice?: (currentOffice: Office) => void;
  profile?: Profile | null;
  latestProfile?: any;
  setProfile?: (profile: Profile) => void;
  prismUserProfile?: PrismUserProfile | null;
  pfNotifications?: number;
  setPfNotifications?: (pfNotifications: number) => void;
  children?: React.ReactNode;
  isAdmin?: boolean;
  setNavigated?: any;
  profileStatus?: 'idle' | 'pending' | 'success' | 'error';
  preferredOffice?: any;
  setpreferredOffice?: (preferredOffice: any) => void;
  getFirmNameByFirmId?: (firmId: string) => string;
  reloadProfile?: () => void;
  getFullNameFromProfile?: () => string;
  isReactivating?: boolean;
  badgeRefreshKey?: number;
  incrementBadgeRefresh?: () => void;
}

const ProfileContext = React.createContext<ProfileContextInterface | null>(null);

const ProfileProvider = (props: ProfileContextInterface): JSX.Element => {
  const { getAccessToken, account, signOut } = useAuth();
  const selectedUserFirm = JSON.parse(localStorage.getItem('userFirm') ?? '{}');
  const [userFirm, setUserFirm] = useState<Firm>(selectedUserFirm ?? { firmId: '', name: '' });
  const [currentOffice, setCurrentOffice] = useState<Office>();
  const storedProfile = JSON.parse(localStorage.getItem('profile') ?? '{}');
  const [profile, setProfile] = useState<Profile>(storedProfile);
  const [latestProfile, setLatestProfile] = useState<any>();
  const [prismUserProfile, setPrismUserProfile] = useState<PrismUserProfile>();
  const [pfNotifications, setPfNotifications] = useState(0);
  const [badgeRefreshKey, setBadgeRefreshKey] = useState(0);
  const [isReactivating, setIsReactivating] = useState(false); // Track reactivation in state
  let selectedPreferredOffice: any = [];
  try {
    selectedPreferredOffice = JSON.parse(localStorage.getItem('preferredOffice') ?? '[]');
  } catch {
    selectedPreferredOffice = [];
  }
  const [preferredOffice, setpreferredOffice] = useState<any>(selectedPreferredOffice);
  const setNavigated = (value: any) => {
    window.localStorage.setItem('isNavigated', value);
  };
  const history = useHistory();
  const isAdmin = profile?.roleId == ADMIN_ROLE_ID;

  const { setTelemetryBaggage } = useTelemetry();
  setTelemetryBaggage(BaggageKeys.UserId, profile?.userId);
  setTelemetryBaggage(BaggageKeys.FirmId, userFirm?.firmId);

  const getProfileData = async (): Promise<Profile> => {
    const token = await getAccessToken();
    const prefferedOfficeresponse = await getPrefferedOffices(token);
    if (prefferedOfficeresponse) {
      window.localStorage.setItem('preferredOffice', JSON.stringify(prefferedOfficeresponse));
      setpreferredOffice(prefferedOfficeresponse);
    }
    return getProfile(token);
  };

  const {
    execute: executeGetProfile,
    value: profileData,
    status: profileStatus,
    errors: profileErrors = [],
  } = useAsync<Profile>(getProfileData, false);
  const reactivateCurrentUserAPI = useCallback(async (): Promise<any | null> => {
    const token = await getAccessToken();
    const response = await reactivateCurrentUser(token);
    return response ?? null;
  }, []);

  const {
    execute: executeReactivateCurrentUser,
    status: reactivateStatus,
    value: reactivateResult,
    errors: reactivateErrors = [],
  } = useAsync<boolean>(reactivateCurrentUserAPI, false);

  const reloadProfile = async () => {
    try {
      await executeGetProfile();
    } catch (error) {
      if (error instanceof NoSessionError) {
        await signOut();
        return;
      }
    }
    const token = await getAccessToken();
    const reponseLatestProfile = await getProfileDetails(token);
    setLatestProfile(reponseLatestProfile);
  };

  useEffect(() => {
    if (!account) return;

    const isUserReactivation = localStorage.getItem('isUserReactivation') === 'true';

    if (isUserReactivation) {
      handleUserReactivation();
    } else {
      reloadProfile();
    }
  }, [account]);

  const handleUserReactivation = async () => {
    setIsReactivating(true);

    try {
      await executeReactivateCurrentUser();
      console.log('User reactivated successfully');
    } catch (error) {
      console.error('Reactivation failed:', error);
    } finally {
      // Always cleanup and reload, regardless of success or failure
      localStorage.removeItem('isUserReactivation');
      await reloadProfile();
      setIsReactivating(false);
    }
  };

  useEffect(() => {
    if (!profileErrors?.length) return;

    const errorString = profileErrors.toString();
    // Check BOTH state AND localStorage to handle race condition
    const isReactivatingFromStorage = localStorage.getItem('isUserReactivation') === 'true';
    const isCurrentlyReactivating = isReactivating || isReactivatingFromStorage;

    if (errorString.includes('User is disabled')) {
      history.push('/authorization/userstatus/403/disabled');
    } else if (errorString.includes('User is Inactive') && !isCurrentlyReactivating) {
      history.push('/authorization/userstatus/403/inactive');
    }
  }, [profileErrors, isReactivating, history]);

  useEffect(() => {
    if (profileStatus === AsyncStatus.ERROR && !profileData) {
      const errorsString = profileErrors?.toString() ?? '';
      const isDisabledError = errorsString.includes('User is disabled');
      const isInactiveError = errorsString.includes('User is Inactive');
      if (!isDisabledError && !isInactiveError) {
        history.push('/authorization/userstatus/404');
      }
    } else if (
      profileStatus === AsyncStatus.SUCCESS &&
      typeof profileData?.userId === 'number' &&
      profileData.userId < 1
    ) {
      history.push('/authorization/userstatus/404');
    }
  }, [profileStatus, profileErrors, profileData]);

  useEffect(() => {
    if (profileData && setProfile) {
      if (!profileData?.userId) signOut().then();

      setProfile(profileData);
      window.localStorage.setItem('profile', JSON.stringify(profileData));
      const defaultFirm = getDefaultFirm(profileData.firms);
      if (userFirm?.firmId) {
        // Keep the firm the user is currently working in, but refresh its offices so changes
        // made elsewhere (e.g. default office updated in Admin -> Users) are reflected without
        // requiring a logout/login (ANS-10331).
        const refreshedFirm = profileData.firms?.find((firm: Firm) => String(firm.firmId) === String(userFirm.firmId));
        if (refreshedFirm) {
          window.localStorage.setItem('userFirm', JSON.stringify(refreshedFirm));
          setUserFirm && setUserFirm(refreshedFirm);
        }
      } else if (defaultFirm) {
        window.localStorage.setItem('userFirm', JSON.stringify(defaultFirm));
        setUserFirm && setUserFirm(defaultFirm);
      }

      const prismUser: PrismUserProfile = {
        firstName: profileData?.firstName ?? '',
        lastName: profileData?.lastName ?? '',
        email: profileData?.emailAddress ?? '',
        primaryPhone: profileData?.phoneNumber
          ? profileData?.phoneNumber.substring(profileData?.phoneNumber.length - 10)
          : '',
        idaaSguid: profileData?.azureObjectId ?? '',
        idaaSuserId: profileData?.login ?? '',
        idaaStype: 'B2C',
      };
      setPrismUserProfile(prismUser);
    }
  }, [profileData]);

  const getFirmNameByFirmId = (firmId: string) => {
    return profileData?.firms?.find((firm: Firm) => String(firm.firmId) === String(firmId))?.name ?? 'N/A';
  };

  const getFullNameFromProfile = () => {
    return getFullName({ firstName: profileData?.firstName, lastName: profileData?.lastName });
  };

  const incrementBadgeRefresh = useCallback(() => setBadgeRefreshKey((k: number) => k + 1), []);

  const ctx: ProfileContextInterface = {
    currentOffice,
    userFirm,
    setCurrentOffice,
    setUserFirm,
    profile,
    latestProfile,
    setProfile,
    prismUserProfile,
    pfNotifications,
    setPfNotifications,
    profileStatus,
    setNavigated,
    setpreferredOffice,
    preferredOffice,
    getFirmNameByFirmId,
    reloadProfile,
    getFullNameFromProfile,
    isReactivating,
    badgeRefreshKey,
    incrementBadgeRefresh,
  };

  return <ProfileContext.Provider value={ctx}>{props.children}</ProfileContext.Provider>;
};

export { ProfileContext, ProfileProvider };
